import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {
  additionalIcons,
  columnTypes,
  ITableOnClickCell,
  ITableRowAction,
  UIDynamicTable,
  UITableHeader,
} from './table.models';

@UntilDestroy()
@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnChanges {
  count: number = 0;
  /**
   * Input that accepts type of the table, that will be displayed.
   * The list of available types provided in tableTypes array.
   */
  @Input() tableType = '';
  /**
   * Input for the table's headers
   */
  @Input() headers!: UITableHeader[];
  /**
   * Input that accepts which edit menu will be showed
   * There are two types - icon and context
   */
  @Input() editMenu = '';
  /**
   * Input for the table's content
   */
  @Input() set content(value: any[]) {
    this.contentArray = value;
    if (this.tableType === 'gross-salary')
      this.editableTableForm.patchValue(value[0]);
  }
  /**
   * Input that accepts total number for accountant tables
   */
  @Input() total!: any;
  /**
   * Input that accepts the rows' background color
   * There are three types - Current, Former and Pending
   */
  @Input() backgroundColorRow = '';

  /**
   * Boolean input that show or hide edit fields
   */
  @Input() showEditFields = false;

  /**
   * list of item which should be muted for input
   */
  @Input() disabledColumnData: string[] = [];
  /**
   * is responsible for emitting items from the table
   */
  @Input() isEmitItemFromTable = false;
  /**
   * is responsible for the ability to sort the table
   */
  @Input() isSortTable = true;
  @Input() showTrashIcon = false;
  @Input() showPlusIcon = true;
  @Input() additionalHeaders!: UITableHeader[];
  /**
   * Count of items for pagination
   */
  @Input() set countPagination(value: number) {
    this.count = value;
  }
  @Input() set editMode(value: boolean) {
    this.showEditFields = value;
  }

  @Input() setFirstPage!: boolean;
  /**
   * Output for emitting the current page
   */
  @Output() pageChanged = new EventEmitter<number>();
  /**
   * Show edit icon only for the last row
   */
  @Input() editOnlyLastRow = false;
  @Input() editLastTwoRows = false;
  @Input() deleteOnlyLastRow = false;
  @Input() displayOnlyFirstRow = false;
  /**
   * Output that emit exact row and action on it
   * Cound use switch case inside the parent for actions
   */
  @Output() rowAction = new EventEmitter<ITableRowAction>();
  /**
   * Emit object with data related to clicked row
   */
  @Output() onClickCell = new EventEmitter<ITableOnClickCell>();
  /**
   * Emit changes of the cell
   * Related to gross salary table
   */
  @Output() onEdit = new EventEmitter();

  /**
   * Emit id related to clicked row in employee tables
   */
  @Output() emitterId = new EventEmitter<number | string>();

  /**
   * Emit interview in interview tables
   */
  @Output() emitterItem = new EventEmitter();

  /**
   * Table's data with headers and content
   */
  public tableData!: UIDynamicTable;
  public columnChecked!: UITableHeader; // which column is clicked
  public type!: string; // type of the sort - increment or decrement
  /**
   * Enum of available column types
   */
  public columnTypes = columnTypes;

  /**
   * Enum of available icons for table
   */
  public icon = additionalIcons;

  /**
   * Reactive form for gross salary table
   */
  public editableTableForm: UntypedFormGroup = new UntypedFormGroup({
    totalUSD: new UntypedFormControl({value: 0, disabled: true}),
    extraBonusUSD: new UntypedFormControl(0, [
      Validators.min(0),
      Validators.max(999999),
    ]),
    otherCompensations: new UntypedFormControl(0, [
      Validators.min(0),
      Validators.max(999999),
    ]),
    grossSalaryUsd: new UntypedFormControl({value: 0, disabled: true}),
    exchangeRate: new UntypedFormControl(0, [
      Validators.min(0),
      Validators.max(99),
    ]),
    grossSalaryUah: new UntypedFormControl({value: 0, disabled: true}),
  });
  public pagination = {page: 1, pageSize: 10};
  public readonly paginationControl = new UntypedFormControl(this.pagination);
  public contentArray!: any;
  public clickedIndex!: number | null;
  /**
   * Array of available table's types
   */
  private tableTypes = [
    'material',
    'employee',
    'interview',
    'material-plus',
    'accountant-table',
    'accountant-table-client',
    'accountant-table-employee',
    'material-main',
    'material-extra',
    'material-vacation',
    'material-salary',
    'gross-salary',
    'salary-table',
    'system-logs',
    'gross-salary-readonly',
  ];

  constructor(private changeDetector: ChangeDetectorRef) {}

  /**
   * Render the table
   * Set value for the form if table type is gross-salary
   * If typed incorrect tableType, set it for material
   */
  ngOnInit(): void {
    this.tableData = {headers: [], data: [], total: {}};
    if (!this.tableTypes.includes(this.tableType)) {
      this.tableType = 'material';
    }
    this.render();
    this.paginationControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(newPage => this.pageChanged.emit(newPage.page));

    this.editableTableForm.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(val => {
        this.onEdit.emit(this.editableTableForm.valid ? val : null);
      });
  }

  /**
   * Method for render the table.
   */
  render() {
    this.tableData = {
      headers: this.headers.filter(
        (element: UITableHeader) => !element.notVisible
      ),
      data: this.contentArray,
      total: this.total,
    };
    this.clickedIndex = null;
    this.changeDetector.detectChanges();
  }

  /**
   * Method for sorting the table
   * @param event standart angular's event
   * @param column which column is clicked
   */
  sortData(event: any, column: UITableHeader) {
    if (this.isSortTable) {
      this.columnChecked = column;
      const target = event.target.innerText;
      if (this.columnChecked.key == '') {
        this.type = 'INC';
      }
      if (target == target && this.type == 'INC') {
        this.type = 'DEC';
      } else {
        this.type = 'INC';
      }
    }
  }

  /**
   * Method click name on table
   * @param id
   * @param selectedValue
   * @param action
   */
  onClickTableCell(
    event: Event,
    id: number,
    selectedValue: string,
    action: string,
    data?: any
  ) {
    event.stopPropagation();
    this.onClickCell.emit({id, selectedValue, action, data});
  }

  ngOnChanges(): void {
    this.render();
  }

  /**
   * Method that emit index of the row and action
   * @param event stantard angular's event
   * @param index index of the clicked row
   * @param action string with clicked action
   */
  rowClick(
    event: Event,
    index: number,
    action: string,
    additionalIndex?: number
  ) {
    event.preventDefault();
    this.rowAction.emit({index, action, additionalIndex});
  }

  displayAdditionalInfo(index: number) {
    if (this.clickedIndex === index) {
      this.clickedIndex = null;
      return;
    }
    this.clickedIndex = index;
  }

  get displayPlusButton() {
    return (
      this.tableType === 'material-plus' &&
      this.showEditFields &&
      this.showPlusIcon
    );
  }
}
